import BaseRepository from "~/api/BaseRepository";
import type { $Fetch } from "nitropack";
import type {Invitation} from "~/api/v1/invitations/InvitationRepository";

export enum ActorAs {
  Participant = 'as_participant',
  Company = 'as_my_companies',
  BusinessProvider = 'as_business_provider',
}

export interface ListMissionsQueryParams {
  limit?: number;
  offset?: number;
  company_id?: string;
  status?: MissionStatus;
  me?: boolean;
  actor_as?: ActorAs;
  emitted?: boolean;
}

export interface MissionResponse {
  mission_id: string;
  name: string;
  description?: string;
  status: MissionStatus;
  freelance_id: string;
  company_id: string;
  waiting_for?: string;
  created_at: string;
  updated_at: string;
  /** True when the mission is a Pass'D (emitted by a sales / non-organization creator). */
  is_passd?: boolean;
  /** Timestamp when company requested mission completion. NULL means no pending request. */
  completion_requested_at?: string;
}

export interface ListMissionsResponse {
  missions: MissionResponse[];
  total: number;
}

export enum MissionStatus {
  CREATED = "open",
  INITIATED = "draft",
  IN_REVIEW = "IN_REVIEW",
  QUOTE_SENT = "QUOTE_SENT",
  QUOTE_ACCEPTED = "QUOTE_ACCEPTED",
  IN_PROGRESS = "active",
  COMPLETION_OPEN = "completion_open",
  COMPLETED = "completed",
  CANCELED = "canceled",
  FILLED = "filled"
}

export interface Mission {
  mission_id: string;
  name: string;
  description?: string;
  status: MissionStatus;
  freelance_id: string;
  company_id: string;
  waiting_for?: string;
  created_at: string;
  updated_at: string;
  emitted: boolean;
  /** True when the mission is a Pass'D (emitted by a sales / non-organization creator). */
  is_passd?: boolean;
  /** Timestamp when company requested mission completion. NULL means no pending request. */
  completion_requested_at?: string;
}

export interface CreateMissionPayload {
  name: string;
  description?: string;
  siren: string;
}

export const MissionRepository = (client: $Fetch) => ({
  ...BaseRepository(client)('/v1/missions'),
  
  fetchAllMissions: (params: ListMissionsQueryParams = { limit: 15, offset: 0 }) => {
    // Build query params
    const queryParams = new URLSearchParams();
    
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.offset) queryParams.append('offset', params.offset.toString());
    if (params.company_id) queryParams.append('company_id', params.company_id);
    if (params.status) queryParams.append('status', params.status);
    if (params.me) queryParams.append('me', params.me ? 'true' : 'false');
    if (params.actor_as) queryParams.append('actor_as', params.actor_as);
    if (params.emitted !== undefined) queryParams.append('emitted', params.emitted ? 'true' : 'false');
    
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    
    return client(`/v2/missions/temp${queryString}`, {
      method: 'GET'
    }) as Promise<ListMissionsResponse>;
  },

  create: (payload: CreateMissionPayload) => {
    const formData = new URLSearchParams();

    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value.toString());
      }
    });

    return client(`/v1/missions`, {
      method: 'POST',
      body: formData.toString(),
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "accept": "application/json"
      }
    }) as Promise<MissionResponse>;
  },
  
  deleteMission: (missionId: string) => {
    return client(`/v1/missions/${missionId}`, {
      method: 'DELETE'
    });
  },

  fillMission: (missionId: string) => {
    return client(`/v1/missions/${missionId}/fill`, {
      method: 'POST'
    });
  },

  invite: (id: string, body: { user_id: string }) => {
    console.log(`Inviting user with ID ${body.user_id} to mission with ID ${id}`);
    return client(`/v1/missions/${id}/invite`, {
      body: body,
      method: 'POST',
      headers: {
        "Content-Type": "application/json",
        "accept": "application/json"
      }
    }) as Promise<Invitation>;
  },

  join: (id: string, body: { user_id: string }) => {
    console.log(`Join user with ID ${body.user_id} to mission with ID ${id}`);
    return client(`/v1/missions/${id}/join`, {
      body: body,
      method: 'POST',
      headers: {
        "Content-Type": "application/json",
        "accept": "application/json"
      }
    }) as Promise<Invitation>;
  },

  updateMissionStatus: (missionId: string, newStatus: MissionStatus, reason?: string) => {
    const formData = new URLSearchParams();
    formData.append('new_status', newStatus);
    if (reason) {
      formData.append('reason', reason);
    }

    return client(`/v1/missions/${missionId}/status`, {
      method: 'POST',
      body: formData.toString(),
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "accept": "application/json"
      }
    });
  },

  initiateCompletion: (missionId: string, companyId: string) => {
    const formData = new URLSearchParams();
    formData.append('new_status', MissionStatus.COMPLETION_OPEN);

    return client(`/v1/missions/${missionId}/status?company_id=${companyId}`, {
      method: 'POST',
      body: formData.toString(),
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "accept": "application/json"
      }
    });
  },

  acceptCompletion: (missionId: string, companyId: string) => {
    const formData = new URLSearchParams();
    formData.append('new_status', MissionStatus.COMPLETED);

    return client(`/v1/missions/${missionId}/status?company_id=${companyId}`, {
      method: 'POST',
      body: formData.toString(),
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "accept": "application/json"
      }
    });
  },

  refuseCompletion: (missionId: string, companyId: string, reason: string) => {
    const formData = new URLSearchParams();
    formData.append('new_status', MissionStatus.IN_PROGRESS);
    formData.append('reason', reason);

    return client(`/v1/missions/${missionId}/status?company_id=${companyId}`, {
      method: 'POST',
      body: formData.toString(),
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "accept": "application/json"
      }
    });
  }
});
